// Executive Assistant — v1.1 §8. Built in Step 8.
//
// THIS IS A STUB. The session that builds the EA edits this file and its own
// modules beside it, never `App.js`.
//
// C2 AND C3 — THE REAL WORK IS THE INVERSE OF THE MOCKUP
// =====================================================
// `executive_assistant.html` shows four things that DO NOT EXIST: an
// "Approve & send" action on a drafted email, an "Upcoming Meetings" tab,
// "3 commitments detected from 10:00 sync", and a green "Online" indicator.
// §8.3 names meeting intelligence as a TARGET; the mockup presents it as
// working. Shipping any of it produces a surface that lies about the product.
// **Do not carry any of the four into this build.**
//
// Meanwhile `/api/me/ea` exists with 16 routes and ZERO consumers today:
// onboarding, lifecycle, memory inspection, Brain requests, provisioning
// health. The mockup surfaces none of them. So Step 8 is: drop the four
// fictions, surface the five realities — which is mostly additive wiring of an
// unconsumed API, the safest kind of work in this document.
//
// WHY "UPCOMING MEETINGS" IS ABSENT FROM THE X-AXIS RATHER THAN RESERVED
// =====================================================================
// SHELL.md lists it as the fourth EA entry. C2 says not to carry it, and unlike
// C1 it defers to no later step. A reserved tab is still a promise rendered on
// screen, so it is absent from `areas.js` entirely. This is a deliberate
// deviation from SHELL.md and is recorded in the night's log for the Founder,
// not made silently.
//
// THE EA DOCK IS THE SHELL'S, NOT THIS SURFACE'S
// ==============================================
// §8.1: the EA follows the operator across Cipher as a contextual side dock,
// hidden only on this area. The shell owns it. Conversation is the EA home
// itself, not an X-axis item (SHELL.md).
//
// R6 — the assistant's name comes from the session, never a literal. "Donna"
// is the FOUNDER's assistant, not every operator's.
//
// THE ONBOARDING GATE (DONNAP-REVIEW-AND-EA-ONBOARDING-LOCKED, 2026-08-21)
// ==========================================================================
// Nick locked an 11-question onboarding questionnaire and ruled it a hard
// gate: the EA does not go live/usable for a user until
// `onboarding_complete` is true, no skip option. `EAOnboarding` (in `./ea/`)
// is that questionnaire; this file mounts it in front of every tab — not
// just the index — whenever a real, authenticated read of `/api/me/ea`
// comes back incomplete. The three tabs below stay exactly what they were
// (still not built) once onboarding is done; this session's job was the
// gate and the questionnaire behind it, not Daily Brief/Priorities/
// Follow-ups.
//
// With `CIPHER_PERSONAL_EA_V1` off, `/api/me/ea` 404s and `EAOnboarding`
// itself renders the "not enabled" state — so this gate is inert in
// production today, by construction, and needs no further change when the
// flag is switched on.
import React, { useEffect, useState } from "react";

import Conversation from "./ea/Conversation";
import DonnaOrb, { WaveBars } from "./ea/DonnaOrb";
import FollowUps from "./ea/FollowUps";
import PageHead from "../components/PageHead";
import Priorities from "./ea/Priorities";
import { useShell } from "../ShellContext";
import { get, ENDPOINTS } from "./ea/api";
import EAOnboarding from "./ea/EAOnboarding";
import "./ea/eaOnboarding.css";
import "./ea/conversation.css";
import "./ea/panels.css";

// #331 stage 2: Priorities and Follow-ups are built. Both routes have existed
// on the backend since August with no consumer, which is why the header above
// described these two tabs as "still not built" and why they rendered the
// empty state. `Component` is what changed; the titles are unaltered.
const PAGES = {
  "": {
    title: "Executive Assistant",
    subtitle: "Your assistant, and what it has for you today.",
    Component: null,
  },
  priorities: {
    title: "Priorities",
    subtitle: "What your assistant thinks matters most right now.",
    Component: Priorities,
  },
  "follow-ups": {
    title: "Follow-ups",
    subtitle: "What is waiting on someone, and for how long.",
    Component: FollowUps,
  },
};

export default function EAArea() {
  const { tab, user } = useShell();
  const page = PAGES[tab] || PAGES[""];
  // "idle" | "thinking" | "replying", reported up by the conversation.
  const [activity, setActivity] = useState("idle");

  // "checking" | "gated" | "clear". Starts "checking" and renders exactly
  // what this area always rendered (the stub below) until the read resolves
  // — a synchronous first render identical to before this gate existed,
  // rather than a blank frame while the request is in flight. A non-"ok"
  // read (disabled/denied/error/offline) resolves straight to "clear" for
  // the same reason: that stub is the correct thing to show when the EA
  // runtime cannot be reached.
  const [gate, setGate] = useState("checking");
  // The shell record, so the home can name the assistant from the owner's own
  // setup rather than from a literal, and know whether it is live.
  const [shell, setShell] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await get(ENDPOINTS.myEA);
      if (!alive) return;
      const complete = res.status === "ok" && res.data?.ea?.onboarding_complete;
      if (res.status === "ok") setShell(res.data?.ea || null);
      setGate(res.status === "ok" && !complete ? "gated" : "clear");
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (gate === "gated") {
    return (
      <>
        <PageHead
          title="Executive Assistant"
          subtitle="A few quick questions before your assistant goes live."
        />
        <EAOnboarding onComplete={() => setGate("clear")} />
      </>
    );
  }

  // #331 stage 1: the conversation IS the home, as this file's header always
  // said it would be. The two remaining tabs are still honestly empty — stage
  // 2 builds Priorities and Follow-ups, and claiming them now would be the
  // fifth fiction this file exists to refuse.
  // An unrecognised tab falls back to the home exactly as `PAGES[tab] ||
  // PAGES[""]` above already intends. Testing `!tab` alone would send an
  // unknown tab down the panel branch, where there is no component to render.
  const isHome = !tab || !PAGES[tab];
  const assistantName = assistantNameFrom(shell);
  const live = (shell?.lifecycle || "dormant") === "live";

  // DESIGN C (Nick, 2026-10-21): the assistant's presence on the left, the
  // one continuous conversation on the right. The orb's state is reported up
  // from the conversation rather than guessed here, so it can only ever show
  // something that is actually happening.
  if (!isHome) {
    const Panel = page.Component;
    return (
      <>
        <PageHead title={page.title} subtitle={page.subtitle} />
        <Panel />
      </>
    );
  }

  const statusLabel = !live ? "Dormant"
    : activity === "thinking" ? "Thinking"
      : activity === "replying" ? "Replying"
        : "Ready";

  return (
    <div className="ea-home">
      <aside className="ea-presence">
        <DonnaOrb state={live ? activity : "idle"} />
        <div className="ea-presence-id">
          <h1>{assistantName}</h1>
          <div className={`ea-presence-status ea-status-${live ? activity : "dormant"}`}>
            {activity === "replying" && live ? <WaveBars /> : null}
            <span>{statusLabel}</span>
          </div>
        </div>
        {/* Shown but inactive: voice is stage 3, and a button that looks
            ready and does nothing is worse than one that says so. */}
        <button
          type="button"
          className="ea-voice-btn"
          disabled
          title="Voice conversation is not built yet"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="9" y="3" width="6" height="11" rx="3" />
            <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
          </svg>
          Talk to {assistantName}
        </button>
        <p className="ea-voice-note">Voice arrives in a later stage.</p>
      </aside>

      <main className="ea-conv-pane">
        <Conversation
          assistantName={assistantName}
          ownerName={firstNameOf(user)}
          live={live}
          onActivityChange={setActivity}
        />
      </main>
    </div>
  );
}

/** The owner's first name for the greeting, or nothing. Never invented. */
export function firstNameOf(user) {
  const n = (user && (user.name || user.full_name)) || "";
  const first = String(n).trim().split(/\s+/)[0];
  return first || null;
}

/** The owner's own name for their assistant, never a literal.
 *
 *  "Donna" is the founder's assistant. Every operator names their own, and
 *  until they have, the surface says "your assistant" rather than guessing.
 */
export function assistantNameFrom(shell) {
  const ob = (shell && shell.onboarding) || {};
  const named = (shell && shell.assistant_name) || ob.assistant_name || ob.name;
  return (typeof named === "string" && named.trim()) || "your assistant";
}
